import React, { useEffect, useState } from "react";
import { Search, PackageCheck } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { TagPreview } from "../lib/tagShapes";
import { WavyBreak } from "../components/Wavy";
import { Sparkle } from "../components/Decor2";

const STEPS = [
  { key: "new", label: "ORDER RECEIVED" },
  { key: "in_production", label: "IN PRODUCTION" },
  { key: "completed", label: "COMPLETED" },
  { key: "shipped", label: "SHIPPED" },
];

const Timeline = ({ status }) => {
  if (status === "cancelled") {
    return <p className="mt-6 text-center font-serif-display uppercase tracking-[0.15em] text-plum" data-testid="track-cancelled">THIS ORDER WAS CANCELLED — CONTACT US IF THAT'S A SURPRISE.</p>;
  }
  const idx = STEPS.findIndex((s) => s.key === status);
  return (
    <div className="mt-8 flex items-start justify-between max-w-lg mx-auto" data-testid="track-timeline">
      {STEPS.map((s, i) => (
        <div key={s.key} className="flex-1 flex flex-col items-center relative">
          {i > 0 && <span className={`absolute top-4 right-1/2 w-full h-[3px] ${i <= idx ? "bg-magenta" : "bg-plum-deep/15"}`} />}
          <span className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center text-cream text-xs font-bold ${i <= idx ? "bg-magenta" : "bg-plum-deep/20"}`}>
            {i + 1}
          </span>
          <span className={`mt-2 text-[10px] uppercase tracking-[0.12em] font-semibold text-center ${i <= idx ? "text-magenta" : "text-plum-deep/40"}`}>{s.label}</span>
        </div>
      ))}
    </div>
  );
};

const TrackOrder = () => {
  const [orderNumber, setOrderNumber] = useState("");
  const [email, setEmail] = useState("");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [paying, setPaying] = useState(false);
  const [bank, setBank] = useState(null);

  useEffect(() => {
    api.get("/workshop/config").then((r) => setBank({
      bank_account_name: r.data.settings?.bank_account_name,
      bank_account_number: r.data.settings?.bank_account_number,
    })).catch(() => {});
  }, []);

  const payNow = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/order-checkout", { order_id: order.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not open the card checkout");
      setPaying(false);
    }
  };

  const lookup = async (e) => {
    e.preventDefault();
    setError("");
    setOrder(null);
    setBusy(true);
    try {
      const { data } = await api.get("/orders/track", { params: { order_number: orderNumber, email } });
      setOrder(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Order not found");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-cream min-h-[70vh]">
      <section className="relative pt-12 md:pt-20 pb-10 px-6 md:px-10 text-center overflow-hidden">
        <Sparkle className="absolute top-16 left-14 w-6 h-6" color="#EC2675" />
        <Sparkle className="absolute top-24 right-20 w-5 h-5" color="#4ECDE9" />
        <span className="font-serif-display uppercase tracking-[0.3em] text-magenta text-[11px] md:text-sm font-semibold">WHERE ARE MY TAGS?</span>
        <h1 className="font-serif-display uppercase text-plum-deep text-4xl sm:text-5xl mt-4" data-testid="track-title">TRACK YOUR ORDER</h1>
      </section>

      <WavyBreak color="#EC2675" />

      <section className="px-6 md:px-10 py-12 max-w-[720px] mx-auto pb-24">
        <form onSubmit={lookup} className="bg-warmwhite border border-plum-deep/10 rounded-[2rem] p-6 md:p-8 flex flex-col sm:flex-row gap-4" data-testid="track-form">
          <input required value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} placeholder="Order number (e.g. BF-1001)" data-testid="track-order-number" className="flex-1 rounded-full px-6 py-3.5 bg-cream border border-plum-deep/15 focus:outline-none focus:border-magenta" />
          <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email used on the order" data-testid="track-email" className="flex-1 rounded-full px-6 py-3.5 bg-cream border border-plum-deep/15 focus:outline-none focus:border-magenta" />
          <button type="submit" disabled={busy} data-testid="track-submit" className="inline-flex items-center justify-center gap-2 rounded-full bg-magenta text-cream px-7 py-3.5 font-serif-display uppercase text-sm tracking-[0.16em] font-semibold hover:bg-plum disabled:opacity-50">
            <Search className="w-4 h-4" /> {busy ? "LOOKING…" : "TRACK"}
          </button>
        </form>

        {error && <p className="mt-5 text-center text-red-600" data-testid="track-error">{error}</p>}

        {order && (
          <div className="mt-8 bg-warmwhite border border-plum-deep/10 rounded-[2rem] p-6 md:p-10" data-testid="track-result">
            <div className="flex items-center justify-center gap-3">
              <PackageCheck className="w-6 h-6 text-forest" />
              <h2 className="font-serif-display uppercase text-plum-deep text-2xl tracking-[0.06em]">ORDER {order.order_number}</h2>
            </div>
            <Timeline status={order.status} />
            {order.tracking_number && (
              <p className="mt-6 text-center text-sm bg-forest/10 text-forest rounded-full py-2.5 px-4 font-semibold" data-testid="track-tracking-number">
                TRACKING NUMBER: {order.tracking_number}
              </p>
            )}
            <div className="mt-8 border-t border-plum-deep/10 pt-6">
              <ul className="space-y-3">
                {order.items?.map((it, i) => (
                  <li key={i} className="flex items-center gap-4 bg-cream rounded-2xl p-3">
                    <TagPreview shapeKey={it.shape_key} hex={it.colour_hex} line1={it.text_line1} line2={it.text_line2} size={it.size} icon={it.icon} className="w-10 h-14 shrink-0" />
                    <div className="flex-1 min-w-0 text-sm">
                      <p className="font-medium truncate">{it.bundle_name ? it.bundle_name : `"${it.text_line1}"${it.text_line2 ? ` / ${it.text_line2}` : ""}`}</p>
                      <p className="text-xs text-plum-deep/60">{it.bundle_name ? `${it.texts?.length || 0} tags · ` : ""}{it.shape_name} · {it.colour_name} · ×{it.quantity}</p>
                    </div>
                    <span className="text-sm font-semibold">${it.line_total?.toFixed(2)}</span>
                  </li>
                ))}
              </ul>
              {order.discount > 0 && (
                <p className="mt-4 flex justify-between text-sm text-forest font-serif-display">
                  <span className="uppercase tracking-[0.1em]">QUANTITY DISCOUNT ({order.discount_percent}%)</span>
                  <span data-testid="track-discount">−${order.discount.toFixed(2)}</span>
                </p>
              )}
              {order.voucher_amount > 0 && (
                <p className="mt-1 flex justify-between text-sm text-forest font-serif-display">
                  <span className="uppercase tracking-[0.1em]">VOUCHER {order.voucher_code}</span>
                  <span data-testid="track-voucher">−${order.voucher_amount.toFixed(2)}</span>
                </p>
              )}
              <p className="mt-5 flex justify-between font-serif-display text-lg">
                <span className="uppercase tracking-[0.1em]">TOTAL</span>
                <span>${order.total?.toFixed(2)} {order.currency}</span>
              </p>
              <p className="mt-1 text-xs text-plum-deep/50 text-right uppercase tracking-[0.14em]">PAYMENT: {order.payment_status}</p>
              {order.payment_status !== "paid" && order.total > 0 && (
                <div className="mt-4 bg-cream rounded-2xl p-4" data-testid="track-payment-box">
                  <button
                    onClick={payNow}
                    disabled={paying}
                    data-testid="track-pay-btn"
                    className="w-full rounded-full bg-magenta text-cream py-3 font-serif-display uppercase text-xs tracking-[0.18em] font-bold hover:bg-plum transition-colors disabled:opacity-50"
                  >
                    {paying ? "OPENING CHECKOUT…" : "PAY NOW BY CARD"}
                  </button>
                  {bank?.bank_account_number && (
                    <p className="mt-3 text-xs text-plum-deep/60 text-center">
                      Or bank transfer: <b>{bank.bank_account_name}</b> · <span className="font-mono">{bank.bank_account_number}</span> · Ref <b>{order.order_number}</b>
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default TrackOrder;
